import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, BellRing, BellOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = [
  { key: 'entry', label: 'New Stage 2', hint: 'A watched stock newly passes all 8 Trend Template rules.' },
  { key: 'sepa', label: 'SEPA Lite pass', hint: 'A watched Stage 2 stock also clears the EPS ≥20% YoY gate.' },
  { key: 'tight_base', label: 'Tight base flagged', hint: 'Price range and volume dry up — a VCP-style candidate to confirm on the chart.' },
  { key: 'exit', label: 'Broke the template', hint: 'A watched Stage 2 stock fails one or more rules; the alert names which.' },
];

// Watchlist-only Stage 2 / SEPA bell-alert settings. Lives inside the
// Watchlist "Alerts" panel beneath the ex-dividend settings.
export default function Stage2AlertSettings({ onStockClick }) {
  const [prefs, setPrefs] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/users/me/stage2-alert-prefs`)
      .then((r) => { if (!cancelled) setPrefs(r.data?.prefs || null); })
      .catch(() => { if (!cancelled) toast.error('Could not load Stage 2 alert settings'); });
    axios.get(`${API}/stage2-alerts/preview`)
      .then((r) => { if (!cancelled) setPreview(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/users/me/stage2-alert-prefs`, patch);
      setPrefs(data?.prefs || null);
    } catch {
      toast.error('Could not save Stage 2 alert settings');
    } finally {
      setSaving(false);
    }
  }, []);

  if (!prefs) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2" data-testid="stage2-settings-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading Stage 2 alert settings…
      </div>
    );
  }

  const inStage2 = (preview?.rows || []).filter((r) => r.trend_template_pass);

  return (
    <div className="space-y-3" data-testid="stage2-alert-settings">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-sky-600 dark:text-sky-400" />
        <h3 className="text-sm font-bold tracking-tight">Stage 2 · Minervini Bell Alerts</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
      </div>
      <p className="text-[11px] text-foreground leading-relaxed">
        <span className="font-bold block">Header-bell notices from the nightly Minervini scan, for non-dividend stocks on your watchlist only.</span>
        <span className="font-bold block mt-1">Pick which changes are worth a ping.</span>
      </p>

      <div className="rounded-lg border border-border bg-card p-3 space-y-3" data-testid="stage2-settings-watchlist">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-bold flex items-center gap-2">
            {prefs.enabled ? <BellRing className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" /> : <BellOff className="w-3.5 h-3.5 text-muted-foreground" />}
            Watchlist Stocks
          </span>
          <input
            type="checkbox"
            className="h-4 w-4 accent-sky-600 cursor-pointer"
            checked={!!prefs.enabled}
            onChange={(e) => save({ enabled: e.target.checked })}
            data-testid="stage2-settings-enabled"
          />
        </label>

        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">Alert on</p>
          <ul className="space-y-1.5">
            {ALERT_TYPES.map((t) => (
              <li key={t.key}>
                <label className={`flex items-start justify-between gap-3 ${prefs.enabled ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}>
                  <span className="min-w-0">
                    <span className="text-[11px] font-bold block">{t.label}</span>
                    <span className="text-[10px] text-muted-foreground block leading-snug">{t.hint}</span>
                  </span>
                  <input
                    type="checkbox"
                    disabled={!prefs.enabled}
                    className="h-4 w-4 mt-0.5 accent-sky-600 cursor-pointer shrink-0"
                    checked={!!prefs[t.key]}
                    onChange={(e) => save({ [t.key]: e.target.checked })}
                    data-testid={`stage2-settings-${t.key}`}
                  />
                </label>
              </li>
            ))}
          </ul>
        </div>

        {prefs.enabled && preview && (
          <div data-testid="stage2-settings-preview">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1.5">
              Watched names in Stage 2 today · {inStage2.length} of {preview.scanned} scanned
            </p>
            {inStage2.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-muted/20 p-2 text-[11px] text-muted-foreground font-semibold text-center" data-testid="stage2-settings-preview-empty">
                {preview.scanned === 0
                  ? 'None of your watched stocks are in the non-dividend Minervini scan yet.'
                  : 'None of your watched non-dividend stocks pass the Trend Template right now — you\u2019ll hear when one does.'}
              </div>
            ) : (
              <ul className="flex flex-wrap gap-1.5" data-testid="stage2-settings-preview-list">
                {inStage2.map((r) => (
                  <li key={r.ticker}>
                    <button
                      type="button"
                      onClick={() => onStockClick?.(r.ticker)}
                      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-300/60 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 hover:brightness-110"
                      data-testid={`stage2-settings-preview-${r.ticker}`}
                    >
                      {r.ticker}
                      <span className="font-mono font-normal">RS {r.rs_rank ?? '—'}</span>
                      <span className="font-mono font-normal">· {r.stage2_days}d</span>
                      {r.sepa_pass && <span className="text-emerald-600 dark:text-emerald-400">SEPA</span>}
                      {r.tight_base && <span className="text-amber-600 dark:text-amber-400">Tight</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
